import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Keyboard, CheckCircle, XCircle, Volume2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const SPELL_WORDS_BY_GRADE = {
  6: [
    { word: 'BEAUTIFUL', hint: 'Pleasing to look at', pronunciation: 'BYOO-tih-ful' },
    { word: 'NECESSARY', hint: 'Required or essential', pronunciation: 'NEH-seh-sair-ee' },
    { word: 'KNOWLEDGE', hint: 'Information and understanding', pronunciation: 'NOL-ij' },
    { word: 'DIFFERENT', hint: 'Not the same', pronunciation: 'DIH-frent' },
    { word: 'TOMORROW', hint: 'The day after today', pronunciation: 'tuh-MOR-oh' },
    { word: 'SURPRISE', hint: 'Something unexpected', pronunciation: 'ser-PRIZE' },
    { word: 'REMEMBER', hint: 'To recall from memory', pronunciation: 'rih-MEM-ber' },
    { word: 'TOGETHER', hint: 'With each other', pronunciation: 'tuh-GEH-ther' },
  ],
  7: [
    { word: 'OCCURRENCE', hint: 'Something that happens', pronunciation: 'uh-KUR-ens' },
    { word: 'ACCOMMODATE', hint: 'To provide lodging', pronunciation: 'uh-KOM-uh-dayt' },
    { word: 'EXAGGERATE', hint: 'To overstate something', pronunciation: 'ig-ZAJ-uh-rayt' },
    { word: 'EMBARRASS', hint: 'To make someone feel shy', pronunciation: 'em-BARE-us' },
    { word: 'ENVIRONMENT', hint: 'The natural world around us', pronunciation: 'en-VY-run-ment' },
    { word: 'GOVERNMENT', hint: 'The ruling body of a nation', pronunciation: 'GUV-urn-ment' },
    { word: 'CONSCIENCE', hint: 'Sense of right and wrong', pronunciation: 'KON-shens' },
    { word: 'RECOMMEND', hint: 'To suggest to others', pronunciation: 'reh-kuh-MEND' },
  ],
  8: [
    { word: 'SURVEILLANCE', hint: 'Close observation', pronunciation: 'ser-VAY-lens' },
    { word: 'BUREAUCRACY', hint: 'Administrative system', pronunciation: 'byoo-ROK-ruh-see' },
    { word: 'ONOMATOPOEIA', hint: 'Words imitating sounds', pronunciation: 'ON-uh-mat-uh-PEE-uh' },
    { word: 'ENTREPRENEURSHIP', hint: 'Starting and running a business', pronunciation: 'on-truh-preh-NUR-ship' },
    { word: 'MEDITERRANEAN', hint: 'A sea between Europe and Africa', pronunciation: 'meh-dih-tuh-RAY-nee-un' },
    { word: 'PHOTOSYNTHESIS', hint: 'How plants make food', pronunciation: 'foh-toh-SIN-thuh-sis' },
    { word: 'ARCHAEOLOGICAL', hint: 'Related to ancient remains', pronunciation: 'ar-kee-uh-LOJ-ih-kul' },
    { word: 'UNPRECEDENTED', hint: 'Never done before', pronunciation: 'un-PREH-sih-den-ted' },
  ]
};

const SpellBee = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const grade = user?.grade_level || 7;
  const SPELL_WORDS = SPELL_WORDS_BY_GRADE[grade] || SPELL_WORDS_BY_GRADE[7];
  const [index, setIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [showWord, setShowWord] = useState(false);
  const total = 8;

  const current = SPELL_WORDS[index];

  const checkSpelling = () => {
    if (userInput.toUpperCase().trim() === current.word) {
      setScore(s => s + 15); setFeedback({ correct: true, msg: 'Perfectly spelled!' });
    } else {
      setFeedback({ correct: false, msg: `Correct spelling: ${current.word}` });
    }
    setShowWord(true);
    setTimeout(() => { if (index < total - 1) { setIndex(i => i+1); setUserInput(''); setFeedback(null); setShowWord(false); } else setCompleted(true); }, 2000);
  };

  if (completed) {
    return (
      <div className="min-h-screen bg-[#F4F8FB] flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-10 shadow-2xl max-w-md w-full text-center border-2 border-slate-100">
          <div className="text-7xl mb-4">🐝</div>
          <h2 className="text-4xl font-black text-[#1E293B] mb-2">Spelling Champion!</h2>
          <p className="text-6xl font-black text-lime-500 mb-2">{score}</p>
          <p className="text-xl text-[#64748B] mb-8">out of {total * 15} points</p>
          <div className="space-y-3">
            <button onClick={() => { setIndex(0); setScore(0); setCompleted(false); setUserInput(''); setFeedback(null); setShowWord(false); }} className="w-full py-4 bg-lime-500 text-white rounded-2xl font-bold border-b-4 border-lime-700 active:border-b-0 active:translate-y-1 transition-all">Play Again</button>
            <button onClick={() => navigate('/games')} className="w-full py-4 bg-slate-100 text-[#1E293B] rounded-2xl font-bold">Back to Brain Arena</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-lime-50 to-green-50" data-testid="spell-bee-game">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate('/games')} className="flex items-center gap-2 text-[#64748B] font-semibold"><ArrowLeft className="w-5 h-5" />Back</button>
          <div className="flex gap-3"><span className="bg-lime-500 text-white px-4 py-2 rounded-2xl font-bold">{index+1}/{total}</span><span className="bg-[#4D96FF] text-white px-4 py-2 rounded-2xl font-bold">{score}pts</span></div>
        </div>
        <div className="bg-white rounded-3xl p-10 border-2 border-slate-100 shadow-xl text-center">
          <Keyboard className="w-12 h-12 text-lime-500 mx-auto mb-4" />
          <h2 className="text-2xl font-black text-[#1E293B] mb-6">Spell the Word!</h2>
          <div className="bg-lime-50 rounded-2xl p-6 mb-6 border-2 border-lime-200">
            <div className="flex items-center justify-center gap-3 mb-3">
              <Volume2 className="w-6 h-6 text-lime-600" />
              <span className="text-2xl font-black text-lime-700 italic">{current.pronunciation}</span>
            </div>
            <p className="text-lg text-[#64748B]">Meaning: {current.hint}</p>
            <p className="text-sm text-[#64748B] mt-2">{current.word.length} letters</p>
          </div>
          {showWord && <div className="flex gap-1 justify-center mb-4 flex-wrap">
            {current.word.split('').map((ch, i) => (
              <span key={i} className="w-10 h-12 bg-lime-100 border-2 border-lime-300 rounded-lg flex items-center justify-center text-xl font-black text-lime-700">{ch}</span>
            ))}
          </div>}
          <input type="text" value={userInput} onChange={(e) => setUserInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && userInput && checkSpelling()}
            className="w-full text-center text-2xl font-bold p-4 bg-[#F4F8FB] border-2 border-slate-200 rounded-2xl focus:border-lime-500 outline-none mb-4 uppercase tracking-widest" placeholder="Type the spelling..." autoFocus data-testid="spell-input" />
          {feedback && <div className={`p-4 rounded-2xl font-bold mb-4 ${feedback.correct ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{feedback.correct ? <CheckCircle className="w-5 h-5 inline mr-2" /> : <XCircle className="w-5 h-5 inline mr-2" />}{feedback.msg}</div>}
          <button onClick={checkSpelling} disabled={!userInput} className="px-10 py-4 bg-lime-500 text-white rounded-2xl font-bold border-b-4 border-lime-700 active:border-b-0 active:translate-y-1 transition-all disabled:opacity-50" data-testid="check-spell-btn">Check Spelling</button>
        </div>
      </div>
    </div>
  );
};

export default SpellBee;
